import type { DictItem } from 'v-dict'

import type { DictApi } from './types'

import type { PagingRequest } from '#/api/type'

import { requestClient } from '#/api'
import { defineApi } from '#/api/define-api'
import { mergeAsyncFn } from '#/utils'

// return requestClient
//       .post(prefix('users/query'), {
//         pageIndex: 1,
//         pageSize: 20,
//       })
//       .then((res) => {
//         return {
//           items: res.list,
//           total: res.total,
//         }
//       })

export const dictApi = defineApi('operation', ({ prefix, mapResponse }) => {
  async function getDict(codes: string[]) {
    return requestClient
      .post<DictApi.GetDictResponse>(prefix('dict/type'), {
        dictTypes: codes,
      } satisfies DictApi.GetDictParams)
      .then((res) =>
        Object.fromEntries(
          codes.map((code) => [
            code,
            mapResponse(res[code], (item) => ({
              label: item?.label,
              value: item?.value,
              code: item?.code,
              ...item,
            })) as DictItem[],
          ]),
        ),
      )
  }

  const getDictMerged = mergeAsyncFn(getDict, {
    argsResolver: (items) => {
      const keys = items.reduce((prev, cur) => {
        const [itemKeys] = cur.args
        prev.push(...itemKeys)
        return prev
      }, [] as string[])
      return [keys] as [string[]]
    },
    resultResolver(result, item) {
      const [keys] = item.args
      return Object.values(keys).reduce(
        (prev, key) => {
          prev[key] = result[key]!
          return prev
        },
        {} as Awaited<ReturnType<typeof getDict>>,
      )
    },
  })

  async function getAllDictTypes() {
    return requestClient
      .get<DictApi.GetAllDictTypesResponse[]>(prefix('dict/getAllDictTypes'))
      .then((res) =>
        res.map((item) => ({
          dictType: item.dictType,
          remark: item.remark,
        })),
      )
  }

  function listDictItems(params: PagingRequest<{ dictType?: string }>) {
    return requestClient
      .post<DictApi.ListDictItemsResponse>(prefix('dict/list'), params)
      .then((res) => ({
        items:
          res?.list.map((item) => ({
            id: item.id,
            dictType: item.dictType,
            dictCode: item.dictCode,
            dictValue: item.dictValue,
            dictLabel: item.dictLabel,
            dictLabelEn: item.dictLabelEn,
            icon: item.icon,
            sortOrder: item.sortOrder,
            status: item.status,
            isDefault: item.isDefault,
            remark: item.remark,
            createdBy: item.createdBy,
            createdTime: item.createdTime,
            updatedBy: item.updatedBy,
            updatedTime: item.updatedTime,
            isDeleted: item.isDeleted,
          })) || [],
        total: res?.total || 0,
      }))
  }

  function createDictItem(params: DictApi.CreateDictItemParams) {
    return requestClient.post(prefix('dict/create'), {
      dictType: params.dictType,
      dictCode: params.dictCode,
      dictValue: params.dictValue,
      dictLabel: params.dictLabel,
      dictLabelEn: params.dictLabelEn,
      icon: params.icon,
      status: Number(params.status),
    })
  }

  function updateDictItem(params: DictApi.UpdateDictItemParams) {
    return requestClient.post(prefix('dict/update'), {
      dictType: params.dictType,
      id: params.id,
      dictCode: params.dictCode,
      dictValue: params.dictValue,
      dictLabel: params.dictLabel,
      dictLabelEn: params.dictLabelEn,
      icon: params.icon,
      status: Number(params.status),
    })
  }

  function updateDictStatus(params: DictApi.UpdateDictStatusParams) {
    return requestClient.post(prefix('dict/updateStatus'), {
      dictType: params.dictType,
      ids: params.ids,
      status: Number(params.status),
    })
  }

  return {
    getDict,
    getDictMerged,
    getAllDictTypes,
    listDictItems,
    createDictItem,
    updateDictItem,
    updateDictStatus,
  }
})
